import React from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import moment from "moment-timezone";

import groupBy from "lodash/groupBy";

import Callout from "ui/Callout";
import ExplainTable from "features/Explain/ExplainTable";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import QueryTags, { QueryTagType } from "features/Queries/QueryTags";
import SQL from "ui/SQL";

import QueryExplainsBlankSlate from "features/Explain/QueryExplainsBlankSlate";
import { useRoutes } from "utils/routes";
import LogProcessingDisabledPanel from "features/Logs/LogProcessingDisabledPanel";

import { useExplainListQuery, ExplainListQuery } from "./gql/Query.generated";

type ExplainType = ExplainListQuery["getExplains"][number];

function ExplainGroup({
  serverId,
  explains,
  blockSize,
}: {
  serverId: string;
  explains: ExplainType[];
  queryId: string;
  blockSize: number;
}) {
  const { serverRole, databaseQueryExplains } = useRoutes();
  const explain = explains[0];
  const mostRecentlySeenAt = explains
    .slice()
    .sort((e1, e2) => e2.seenAt - e1.seenAt)[0].seenAt;
  // for now, we're just taking tags from the first sample that has any;
  // may want to merge them? use latest?
  const explainWithQuerySample = explains.find(
    (e) => e.querySample && e.querySample.queryTagset,
  );
  const querySample =
    explainWithQuerySample && explainWithQuerySample.querySample;
  const postgresRole = explains.every(
    (explain) => explain.postgresRole === explains[0].postgresRole,
  )
    ? explains[0].postgresRole
    : undefined;

  const tags: Array<QueryTagType> = [];
  if (querySample?.queryTagset) {
    const { commentData } = querySample.queryTagset;
    for (const key in commentData) {
      const value = commentData[key];
      tags.push({
        id: key,
        key: key,
        value: typeof value === "string" ? value : JSON.stringify(value),
      });
    }
  }

  const secondaryTitle = (
    <span>
      {postgresRole && (
        <span>
          Role:&nbsp;
          <Link to={serverRole(serverId, postgresRole.id)}>
            {postgresRole.name}
          </Link>
          &nbsp;·&nbsp;
        </span>
      )}
      {moment.unix(mostRecentlySeenAt).format("lll z")}
    </span>
  );

  return (
    <Panel
      title={`EXPLAINs for Query #${explain.query.id}`}
      secondaryTitle={secondaryTitle}
    >
      <PanelSection>
        <SQL sql={explain.query.truncatedQuery} />
        {tags.length > 0 && <QueryTags tags={tags} />}
      </PanelSection>
      <ExplainTable
        databaseId={explain.database.id}
        explains={explains}
        blockSize={blockSize}
      />
      <PanelSection>
        <Link
          to={databaseQueryExplains(explain.database.id, explain.query.id)}
          className="btn btn-success"
        >
          Show Plan
        </Link>
      </PanelSection>
    </Panel>
  );
}

function ExplainList() {
  const { databaseId, serverId } = useParams();
  const { loading, error, data } = useExplainListQuery({
    variables: { databaseId, serverId },
  });

  if ((loading && !data?.getExplains) || error) {
    return <Loading error={!!error} />;
  }
  const server = data.getServerDetails;
  const explains = data.getExplains;

  if (explains.length === 0) {
    if (server.collectorInfo?.logCollectionDisabled) {
      return (
        <LogProcessingDisabledPanel
          disabledReasons={server.collectorInfo.logCollectionDisabledReason}
        />
      );
    }
    return <QueryExplainsBlankSlate serverId={server.humanId} />;
  }

  const explainsByQuery = Object.entries<ExplainType[]>(
    groupBy(explains, (explain) => explain.query.id),
  );

  return (
    <>
      <Callout className="mb-3" thin>
        Showing up to 50 of the most recent EXPLAIN plans. To see more plans for
        a single query, open that query and go to its EXPLAIN Plans tab.
      </Callout>
      {explainsByQuery.map(([queryId, queryExplains]) => (
        <ExplainGroup
          key={queryId}
          serverId={server.humanId}
          explains={queryExplains}
          queryId={queryId}
          blockSize={server.blockSize}
        />
      ))}
    </>
  );
}

export default ExplainList;
